import {
  infiniteQueryOptions,
  keepPreviousData,
  queryOptions,
} from "@tanstack/react-query";
import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";

import { permissionMiddleware } from "~/lib/permission-middleware";
import { ContentStatus } from "~/lib/schemas/db-enums-cih";
import { NOVA_SUCCESS_STORY_SOURCE } from "~/lib/shared/transformation-story";

import {
  GetStoriesPaginatedSchema,
  type GetStoriesPaginated,
} from "../schemas/transformation-story";

export const getTransformationStoriesFn = createServerFn({ method: "GET" })
  .validator((data: unknown) => GetStoriesPaginatedSchema.parse(data))
  .handler(async ({ data }: { data: GetStoriesPaginated }) => {
    const { getTransformationStoriesPaginated } =
      await import("../server/transformation-story");
    return getTransformationStoriesPaginated(data);
  });

export const transformationStoriesQueryKeys = {
  all: ["transformation-stories"] as const,
  paginated: (seed: number) =>
    [...transformationStoriesQueryKeys.all, "paginated", { seed }] as const,
};

export const transformationStoriesInfiniteQueryOptions = (seed: number) =>
  infiniteQueryOptions({
    queryKey: transformationStoriesQueryKeys.paginated(seed),
    queryFn: async ({ pageParam }) => {
      return getTransformationStoriesFn({
        data: {
          page: pageParam,
          limit: 12,
          seed,
        },
      });
    },
    initialPageParam: 1,
    getNextPageParam: (lastPage) =>
      lastPage.pagination.hasNextPage
        ? lastPage.pagination.nextPage
        : undefined,
  });

// ============================================================================
// Admin Functions
// ============================================================================

export type {
  AdminStoryListPage,
  AdminStoryRow,
} from "../server/transformation-story";

const adminStoryListInputSchema = z.object({
  page: z.number().int().min(1),
  pageSize: z.number().int().min(1).max(100),
  search: z.string().trim(),
  status: z.nativeEnum(ContentStatus).nullable(),
});

export type AdminStoryListInput = z.infer<typeof adminStoryListInputSchema>;

export interface SummitSessionOption {
  id: string;
  title: string;
}

/** One page of stories for the admin review list. */
export const listAdminStoriesFn = createServerFn({ method: "GET" })
  .middleware([permissionMiddleware("cih:read")])
  .validator(adminStoryListInputSchema)
  .handler(async ({ data }) => {
    const { listAdminStories } = await import("../server/transformation-story");
    return listAdminStories(data);
  });

/** One story with its review context. */
export const getAdminStoryFn = createServerFn({ method: "GET" })
  .middleware([permissionMiddleware("cih:read")])
  .validator(z.object({ storyId: z.string().uuid() }))
  .handler(async ({ data }) => {
    const { getAdminStory } = await import("../server/transformation-story");
    return getAdminStory(data.storyId);
  });

/**
 * Get all summit sessions for the session link dropdown.
 */
export const getSummitSessionOptions = createServerFn({
  method: "GET",
})
  .middleware([permissionMiddleware("cih:read")])
  .handler(async (): Promise<SummitSessionOption[]> => {
    const { getDBWithInit } = await import("~/lib/db");
    const { summitSessions } = await import("~/../drizzle/schema");
    const { asc } = await import("drizzle-orm");

    const db = await getDBWithInit();

    const sessions = await db
      .select({
        id: summitSessions.id,
        title: summitSessions.title,
      })
      .from(summitSessions)
      .orderBy(asc(summitSessions.title));

    return sessions;
  });

export const adminStoryQueryKeys = {
  all: ["admin", "stories"] as const,
  list: (input: AdminStoryListInput) =>
    [...adminStoryQueryKeys.all, "list", input] as const,
  detail: (storyId: string) =>
    [...adminStoryQueryKeys.all, "detail", storyId] as const,
  summitSessionOptions: () =>
    [...adminStoryQueryKeys.all, "summit-session-options"] as const,
};

export const adminStoriesQueryOptions = (input: AdminStoryListInput) =>
  queryOptions({
    queryKey: adminStoryQueryKeys.list(input),
    queryFn: () => listAdminStoriesFn({ data: input }),
    placeholderData: keepPreviousData,
  });

export const adminStoryQueryOptions = (storyId: string) =>
  queryOptions({
    queryKey: adminStoryQueryKeys.detail(storyId),
    queryFn: () => getAdminStoryFn({ data: { storyId } }),
  });

export const summitSessionOptionsQueryOptions = () =>
  queryOptions({
    queryKey: adminStoryQueryKeys.summitSessionOptions(),
    queryFn: () => getSummitSessionOptions(),
    staleTime: 5 * 60 * 1000,
  });

/** Publish an in-review Nova success story after human editorial review. */
export const publishNovaSuccessStoryFn = createServerFn({ method: "POST" })
  .middleware([permissionMiddleware("cih:write")])
  .validator(
    z.object({
      storyId: z.string().uuid(),
      outcomeVerified: z.boolean().default(false),
    }),
  )
  .handler(async ({ data, context }) => {
    const { publishNovaSuccessStory } =
      await import("../server/transformation-story");

    return publishNovaSuccessStory({
      storyId: data.storyId,
      reviewerId: context.user.id,
      outcomeVerified: data.outcomeVerified,
    });
  });

/** Archive a Nova success story as an editorial decision; consent stays intact. */
export const archiveNovaSuccessStoryFn = createServerFn({ method: "POST" })
  .middleware([permissionMiddleware("cih:write")])
  .validator(z.object({ storyId: z.string().uuid() }))
  .handler(async ({ data }) => {
    const { archiveNovaSuccessStory } =
      await import("../server/transformation-story");
    return archiveNovaSuccessStory(data.storyId);
  });

/** Return an archived Nova success story to the review queue. */
export const restoreNovaSuccessStoryFn = createServerFn({ method: "POST" })
  .middleware([permissionMiddleware("cih:write")])
  .validator(z.object({ storyId: z.string().uuid() }))
  .handler(async ({ data }) => {
    const { restoreNovaSuccessStoryToReview } =
      await import("../server/transformation-story");
    return restoreNovaSuccessStoryToReview(data.storyId);
  });

/** Save editorial fixes to an in-review story's public text. */
export const updateInReviewSuccessStoryBodyFn = createServerFn({
  method: "POST",
})
  .middleware([permissionMiddleware("cih:write")])
  .validator(
    z.object({
      storyId: z.string().uuid(),
      body: z.string().trim().min(1),
    }),
  )
  .handler(async ({ data }) => {
    const { updateInReviewSuccessStoryBody } =
      await import("../server/transformation-story");
    return updateInReviewSuccessStoryBody(data);
  });

/** Archive a Nova success story after the client withdraws public consent. */
export const withdrawSuccessStoryConsentFn = createServerFn({ method: "POST" })
  .middleware([permissionMiddleware("cih:write")])
  .validator(
    z.object({
      storyId: z.string().uuid(),
    }),
  )
  .handler(async ({ data }) => {
    const { withdrawSuccessStoryConsent } =
      await import("../server/transformation-story");

    return withdrawSuccessStoryConsent(data.storyId);
  });

/**
 * Delete a transformation story by ID.
 * Also deletes the associated avatar image from R2 if present.
 */
export const deleteTransformationStory = createServerFn({ method: "POST" })
  .middleware([permissionMiddleware("cih:write")])
  .validator(
    z.object({
      storyId: z.string().uuid(),
    }),
  )
  .handler(async ({ data }): Promise<{ success: boolean }> => {
    const { getDBWithInit } = await import("~/lib/db");
    const { transformationStories } = await import("~/../drizzle/schema");
    const { eq } = await import("drizzle-orm");

    const db = await getDBWithInit();

    // Fetch the story first to get the avatar URL
    const [story] = await db
      .select({
        avatarUrl: transformationStories.avatarUrl,
        source: transformationStories.source,
      })
      .from(transformationStories)
      .where(eq(transformationStories.id, data.storyId));

    if (!story) {
      throw new Error("Transformation story not found.");
    }
    if (story.source === NOVA_SUCCESS_STORY_SOURCE) {
      throw new Error(
        "Nova success stories must be archived by withdrawing consent.",
      );
    }

    // Delete avatar from R2 if present
    if (story.avatarUrl) {
      const { deleteObjectByKey } = await import("~/lib/server/s3");
      const { serverEnv } = await import("~/lib/server-env");

      // Extract key from full URL: https://public-base.com/transformation-stories/avatars/uuid.jpg
      const publicBase = serverEnv.S3_PUBLIC_BASE.replace(/\/$/, "");
      if (story.avatarUrl.startsWith(publicBase)) {
        const key = story.avatarUrl.slice(publicBase.length + 1); // +1 for the /
        try {
          await deleteObjectByKey(key);
        } catch (error) {
          // Log but don't fail the deletion if image cleanup fails
          console.error("Failed to delete avatar from R2:", error);
        }
      }
    }

    await db
      .delete(transformationStories)
      .where(eq(transformationStories.id, data.storyId));

    return { success: true };
  });

/**
 * Update the summit session link for a transformation story.
 */
export const updateStorySessionLink = createServerFn({ method: "POST" })
  .middleware([permissionMiddleware("cih:write")])
  .validator(
    z.object({
      storyId: z.string().uuid(),
      summitSessionId: z.string().uuid().nullable(),
    }),
  )
  .handler(async ({ data }): Promise<{ success: boolean }> => {
    const { getDBWithInit } = await import("~/lib/db");
    const { transformationStories } = await import("~/../drizzle/schema");
    const { eq } = await import("drizzle-orm");

    const db = await getDBWithInit();

    await db
      .update(transformationStories)
      .set({ summitSessionId: data.summitSessionId })
      .where(eq(transformationStories.id, data.storyId));

    return { success: true };
  });

// ============================================================================
// Summit Recap Functions
// ============================================================================

export type { SummitRecapStats } from "../server/transformation-story";

/**
 * Get stats for a summit recap page.
 */
export const getSummitRecapStatsFn = createServerFn({ method: "GET" })
  .validator(z.object({ summitId: z.string().uuid() }))
  .handler(async ({ data }) => {
    const { getSummitRecapStats } =
      await import("../server/transformation-story");
    return getSummitRecapStats(data.summitId);
  });

/**
 * Get transformation stories for a specific summit.
 */
export const getSummitTransformationStoriesFn = createServerFn({
  method: "GET",
})
  .validator(z.object({ summitId: z.string().uuid() }))
  .handler(async ({ data }) => {
    const { getSummitTransformationStories } =
      await import("../server/transformation-story");
    return getSummitTransformationStories(data.summitId);
  });

/**
 * Get basic summit info by ID.
 */
export const getSummitByIdFn = createServerFn({ method: "GET" })
  .validator(z.object({ summitId: z.string().uuid() }))
  .handler(async ({ data }) => {
    const { getSummitById } = await import("../server/transformation-story");
    return getSummitById(data.summitId);
  });

export const summitRecapQueryKeys = {
  stats: (summitId: string) => ["summit-recap", "stats", summitId] as const,
  stories: (summitId: string) => ["summit-recap", "stories", summitId] as const,
};

export const summitRecapStatsQueryOptions = (summitId: string) =>
  queryOptions({
    queryKey: summitRecapQueryKeys.stats(summitId),
    queryFn: () => getSummitRecapStatsFn({ data: { summitId } }),
    staleTime: 5 * 60 * 1000,
  });

export const summitRecapStoriesQueryOptions = (summitId: string) =>
  queryOptions({
    queryKey: summitRecapQueryKeys.stories(summitId),
    queryFn: () => getSummitTransformationStoriesFn({ data: { summitId } }),
    staleTime: 5 * 60 * 1000,
  });
